import { useState } from 'react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import Reveal from '../components/site/Reveal';
import { BRAND } from '../data/site';
import { Mail, Phone, MapPin, Send, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: '', email: '', organization: '', subject: '', message: '', inquiry_type: 'general' };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/inquiries`, form);
      toast.success('Your inquiry has been received. Thank you.');
      setForm(initial);
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || 'Could not submit. Please try again.';
      toast.error(typeof msg === 'string' ? msg : 'Could not submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <Toaster theme="dark" position="top-right" richColors />

      <section className="pt-40 pb-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <Reveal>
            <p className="eyebrow mb-6">— Inquire</p>
            <h1 className="font-display text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tighter text-prim max-w-4xl">
              The door is always <span className="italic text-brand-teal-light">slightly open.</span>
            </h1>
            <p className="mt-8 text-sec text-lg md:text-xl max-w-2xl leading-relaxed">
              Inquiries from operators, limited partners and institutional partners are read personally — and answered in the same hand.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 border-t border-subtle">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="eyebrow mb-5">— Headquarters</p>
                <h3 className="font-display text-3xl text-prim tracking-tight">Doha to the World</h3>

                <ul className="mt-8 space-y-5 text-sec">
                  <li className="flex gap-4">
                    <MapPin size={18} className="text-brand-teal-light mt-1 shrink-0" />
                    <span>{BRAND.contact.address}<br/>{BRAND.contact.city}</span>
                  </li>
                  <li className="flex gap-4 items-center">
                    <Phone size={18} className="text-brand-teal-light shrink-0" />
                    <a href={`tel:${BRAND.contact.phone.replace(/\s/g,'')}`} className="hover-line">{BRAND.contact.phone}</a>
                  </li>
                  <li className="flex gap-4 items-center">
                    <Mail size={18} className="text-brand-teal-light shrink-0" />
                    <a href={`mailto:${BRAND.contact.emailFinance}`} className="hover-line">{BRAND.contact.emailFinance}</a>
                  </li>
                  <li className="flex gap-4 items-center">
                    <Mail size={18} className="text-brand-teal-light shrink-0" />
                    <a href={`mailto:${BRAND.contact.emailInquiry}`} className="hover-line">{BRAND.contact.emailInquiry}</a>
                  </li>
                </ul>

                <div className="mt-12 border-t border-subtle pt-8">
                  <p className="font-arabic text-3xl text-brand-teal-light/80">وَاللَّهُ خَيْرُ الرَّازِقِينَ</p>
                  <p className="mt-2 text-muted italic">— And God is the best of providers.</p>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-7">
              <Reveal delay={150}>
                <form onSubmit={onSubmit} noValidate data-testid="inquiry-form" className="border border-subtle surface-2 p-6 md:p-10 rounded-md">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-mono uppercase tracking-[0.2em] text-muted mb-2">Name *</label>
                      <input
                        name="name" required value={form.name} onChange={onChange}
                        data-testid="inquiry-name" className="lux-input" placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-mono uppercase tracking-[0.2em] text-muted mb-2">Email *</label>
                      <input
                        type="email" name="email" required value={form.email} onChange={onChange}
                        data-testid="inquiry-email" className="lux-input" placeholder="name@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-mono uppercase tracking-[0.2em] text-muted mb-2">Organisation</label>
                      <input
                        name="organization" value={form.organization} onChange={onChange}
                        data-testid="inquiry-organization" className="lux-input" placeholder="Company / family office"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-mono uppercase tracking-[0.2em] text-muted mb-2">Inquiry type</label>
                      <select
                        name="inquiry_type" value={form.inquiry_type} onChange={onChange}
                        data-testid="inquiry-type" className="lux-input"
                      >
                        <option value="general">General</option>
                        <option value="investor">Investor / LP</option>
                        <option value="operator">Operator / Founder</option>
                        <option value="partnership">Strategic partnership</option>
                        <option value="press">Press / media</option>
                      </select>
                    </div>
                  </div>

                  <div className="mt-5">
                    <label className="block text-xs font-mono uppercase tracking-[0.2em] text-muted mb-2">Subject *</label>
                    <input
                      name="subject" required value={form.subject} onChange={onChange}
                      data-testid="inquiry-subject" className="lux-input" placeholder="A line on what you'd like to discuss"
                    />
                  </div>

                  <div className="mt-5">
                    <label className="block text-xs font-mono uppercase tracking-[0.2em] text-muted mb-2">Message *</label>
                    <textarea
                      name="message" required rows={6} value={form.message} onChange={onChange}
                      data-testid="inquiry-message" className="lux-input resize-none" placeholder="Tell us a little about the opportunity, mandate or question."
                    />
                  </div>

                  <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
                    <p className="text-xs text-muted max-w-md">By submitting you agree your inquiry is read by the MBK office. Stored under our PDPPL- and GDPR-aligned data handling controls.</p>
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="inquiry-submit"
                      className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {loading ? <><Loader2 size={16} className="animate-spin" /> Sending</> : <>Send Message <Send size={14} /></>}
                    </button>
                  </div>
                </form>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
